/**
 * Shared JWT storage and refresh (single-flight) for apiClient and auth bootstrap.
 *
 * **Every function here is a no-op once `MAMBA_AUTH_CUTOVER` is on.** After the
 * cutover the session cookie is the only credential the browser holds; there is
 * no access token to persist, no refresh token to rotate, and writing either to
 * `localStorage` would put back exactly what the BFF exists to remove
 * (ADR-0001 §4.1).
 */
import { BFF_LOGIN_PATH, MAMBA_AUTH_CUTOVER } from './authMode';

export interface TokenPair {
  access: string;
  refresh?: string;
}

export const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    const origin = window.location.origin;
    return origin.endsWith('/api') ? origin : `${origin}/api`;
  }
  const url = process.env.REACT_APP_API_URL || 'http://localhost:8001';
  return url.endsWith('/api') ? url : `${url}/api`;
};

export function persistTokens(tokens: TokenPair): void {
  if (MAMBA_AUTH_CUTOVER) {
    // Nothing to keep: the credential is the cookie the server set.
    return;
  }
  localStorage.setItem('accessToken', tokens.access);
  if (tokens.refresh) {
    localStorage.setItem('refreshToken', tokens.refresh);
  }
}

export function clearAuthTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

export function getLoginRedirectPath(): string {
  if (MAMBA_AUTH_CUTOVER) {
    // The BFF starts the Authorization Code flow; the SPA never sees a
    // credential and so has no login form of its own to route to.
    return BFF_LOGIN_PATH;
  }
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || '';
    if (path.startsWith('/platform/admin')) return '/platform/admin/login';
    if (window.location.hostname.toLowerCase().startsWith('admin.')) return '/platform/admin/login';
  }
  return '/login';
}

export function redirectToLogin(): void {
  clearAuthTokens();
  if (typeof window !== 'undefined') {
    window.location.href = getLoginRedirectPath();
  }
}

let refreshPromise: Promise<boolean> | null = null;

/** Refresh access token; persists rotated refresh when returned. Single-flight for concurrent 401s. */
export async function refreshAccessToken(): Promise<boolean> {
  if (MAMBA_AUTH_CUTOVER) {
    // The session is refreshed server-side, inside the BFF, using a refresh
    // token this code has never seen. A 401 here means the session is over,
    // and the honest response is to start a new one rather than retry.
    return false;
  }
  const storedRefresh = localStorage.getItem('refreshToken');
  if (!storedRefresh) {
    return false;
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: storedRefresh }),
        credentials: 'include',
      });
      if (!response.ok) {
        return false;
      }
      const data = (await response.json()) as { access?: string; refresh?: string };
      if (!data.access) {
        return false;
      }
      persistTokens({ access: data.access, refresh: data.refresh });
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
